import React, { useMemo } from "react";

import {
  DesignElement,
  RequirementLevel1Policy,
  RequirementLevel2Policy,
  RequirementLevel3Policy,
} from "@design-controls/types";
import { QAlert, QStack } from "@qualio/ui-components";
import { useTranslation } from "react-i18next";

import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { PolicyIssueEnum } from "@design-controls/types";

type GapAnalysisProps = Pick<DesignElement, "policyIssues" | "type">;

const requirementLevels: Array<DesignElement["type"]> = [
  "req1",
  "req2",
  "req3",
];

type Requirements =
  | RequirementLevel1Policy
  | RequirementLevel2Policy
  | RequirementLevel3Policy;

export const GapAnalysis: React.FC<GapAnalysisProps> = ({
  policyIssues: policyIssueRecords,
  type,
}) => {
  const { t } = useTranslation();
  const configs = useCurrentConfigs();
  const policiesDefaultOrder = Object.values(PolicyIssueEnum);
  const policyIssues = policiesDefaultOrder.filter(
    (policy) => policyIssueRecords[policy],
  );

  const parentRequirement: Requirements | undefined = useMemo(() => {
    if (!type.startsWith("req") || requirementLevels[0] === type) {
      return;
    }

    const parentType =
      requirementLevels[
        requirementLevels.findIndex((level) => level === type) - 1
      ];
    const config = configs.find((config) => config.type === parentType);

    return config as Requirements;
  }, [configs, type]);

  const childRequirement: Requirements | undefined = useMemo(() => {
    if (
      !type.startsWith("req") ||
      requirementLevels[requirementLevels.length - 1] === type
    ) {
      return;
    }

    const childType =
      requirementLevels[
        requirementLevels.findIndex((level) => level === type) + 1
      ];
    const config = configs.find((config) => config.type === childType);

    return config as Requirements;
  }, [configs, type]);

  const policyMessages = useMemo(() => {
    return policyIssues.map((policyIssue) => {
      let title;
      let description;
      switch (policyIssue) {
        case PolicyIssueEnum.FAILING_TEST:
          title = t("openIssues.descriptions.failingTests.title");
          description = t("openIssues.descriptions.failingTests.description");
          break;
        case PolicyIssueEnum.OUTDATED_TESTRESULT:
          title = t("openIssues.descriptions.outdatedTestResult.title");
          description = t(
            "openIssues.descriptions.outdatedTestResult.description",
          );
          break;
        case PolicyIssueEnum.REQUIRES_CHILD:
          title = t("openIssues.descriptions.missingRelatedRequirement.title");
          description = t(
            "openIssues.descriptions.missingRelatedRequirement.requiresChild",
            {
              childRequirement: childRequirement?.label,
            },
          );
          break;
        case PolicyIssueEnum.REQUIRES_PARENT:
          title = t("openIssues.descriptions.missingRelatedRequirement.title");
          description = type.startsWith("testCase")
            ? t(
                "openIssues.descriptions.missingRelatedRequirement.testsRequireRequirement",
              )
            : t(
                "openIssues.descriptions.missingRelatedRequirement.requiresParent",
                {
                  parentRequirement: parentRequirement?.label,
                },
              );
          break;
        case PolicyIssueEnum.UNMITIGATED:
          title = t("openIssues.descriptions.unmitigatedRisks.title");
          description = t(
            "openIssues.descriptions.unmitigatedRisks.description",
          );
          break;
        case PolicyIssueEnum.UNTESTED_REQUIREMENT:
          title = t("openIssues.descriptions.missingRelatedTestCase.title");
          description = t(
            "openIssues.descriptions.missingRelatedTestCase.description",
          );
          break;
      }

      return {
        title,
        description,
        policyIssue,
      };
    });
  }, [policyIssues, childRequirement, parentRequirement, t]);

  if (!policyIssues.length) {
    return null;
  }

  return (
    <QStack gap="16px">
      <QAlert
        description={
          policyMessages.length === 1 ? (
            policyMessages[0].description
          ) : (
            <ul>
              {policyMessages.map((policyMessage) => (
                <li data-cy={policyMessage.policyIssue}>
                  {policyMessage.description}
                </li>
              ))}
            </ul>
          )
        }
        title={
          policyMessages.length === 1
            ? policyMessages[0].title
            : t("openIssues.title")
        }
        status="warning"
        data-cy={
          policyMessages.length === 1
            ? policyMessages[0].policyIssue
            : undefined
        }
      />
    </QStack>
  );
};

// ensure QHeader can show component
GapAnalysis.displayName = "QAlert";
